<template>
  <div>
    <v-card color="transparent" elevation="0" v-if="dropdown">
      <v-autocomplete
        :disabled="disabled"
        :items="filteredList"
        :label="dropdown.label"
        :multiple="multiple"
        chips
        data-cy="dynamicDropdownField"
        deletable-chips
        dense
        filled
        item-text="fullLabel"
        item-value="id"
        small-chips
        v-model="selectedValue"
        :hint="hasDeprecatedValues ? 'Deprecated values appear at the bottom of the list' : ''"
        :persistent-hint="hasDeprecatedValues"
      >
        <template v-slot:item="{ item, attrs, on }">
          <v-list-item v-bind="attrs" v-on="on">
            <v-list-item-action v-if="multiple"><v-checkbox :input-value="attrs.inputValue"></v-checkbox></v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span :class="{ 'grey--text text--darken-1 font-italic': isItemDeprecated(item) }">
                  {{ item.fullLabel }}
                  <span v-if="isItemDeprecated(item)" class="deprecated-label"> (DEPRECATED)</span>
                </span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-slot:selection="{ item }">
          <v-chip
            small
            close
            @click:close="removeItem(item)"
            :color="isItemDeprecated(item) ? 'grey lighten-2' : undefined"
          >
            <span :class="{ 'font-italic grey--text': isItemDeprecated(item) }">
              {{ item.fullLabel }}
            </span>
          </v-chip>
        </template>
      </v-autocomplete>

      <v-overlay :absolute="true" :value="!isInitialized">
        <v-icon>fas fa-spinner fa-spin</v-icon>
      </v-overlay>
    </v-card>

    <v-alert dense outlined type="error" v-if="isInitialized && !dropdown"
      >Could not load dropdown values for attribute '{{ attributeName }}'</v-alert
    >
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Vue from "vue";

import DynamicDropdown from "./DynamicDropdown.vue";

export default Vue.extend({
  name: "DynamicDropdown",
  components: {
    DynamicDropdown,
  },
  computed: {
    filteredList() {
      if (!this.dropdown.valueList) {
        return [];
      }

      const mappedItems = this.dropdown.valueList.map(item => {
        if (this.grouperDropdown) {
          const grouperDropdownValue = this.grouperDropdown.valueList.find(
            value => value.id === item.groupDropdownValueId
          );
          item.fullLabel = `APPLICATION: ${grouperDropdownValue?.label} / PRIMARY ACTIVITY: ${item.label}`;
        } else {
          item.fullLabel = item.label;
        }
        return item;
      });

      // Separate active and deprecated values
      const active = mappedItems.filter(item => !this.isItemDeprecated(item));
      const deprecated = mappedItems.filter(item => this.isItemDeprecated(item));

      // Sort each group alphabetically by fullLabel
      const sortByLabel = (a, b) => a.fullLabel.localeCompare(b.fullLabel);

      return [...active.sort(sortByLabel), ...deprecated.sort(sortByLabel)];
    },
    hasDeprecatedValues() {
      if (!this.dropdown || !this.dropdown.valueList) {
        return false;
      }
      return this.dropdown.valueList.some(item => this.isItemDeprecated(item));
    },
    ...mapGetters({
      getDropdownByAttribute: "admin/dropdown/getByAttributeName",
      getDropdownById: "admin/dropdown/getById",
    }),
    ...mapState({
      dropdownList: state => state.admin.dropdown.list,
    }),
  },
  async created() {
    await this.initialize();
  },
  data() {
    return {
      dropdown: null,
      dropdownValueList: [],
      grouperDropdown: null,
      grouperDropdownValueId: null,
      isInitialized: false,
      selectedValue: this.value ?? null,
    };
  },
  methods: {
    async initialize() {
      this.isInitialized = false;
      this.dropdown = null;
      this.grouperDropdown = null;
      this.grouperDropdownValueId = null;

      try {
        await this.getDropdownList();
      } catch (e) {
        console.error(e);
        this.isInitialized = true;
        return;
      }

      this.dropdown = this.getDropdownByAttribute(this.attributeName);

      if (!this.dropdown) {
        console.error(`Dropdown with attributeName '${this.attributeName}' does not exist `);
        this.isInitialized = true;
        return;
      }

      const { groupDropdownId } = this.dropdown;

      if (groupDropdownId) {
        this.grouperDropdown = this.getDropdownById(groupDropdownId);
      }

      this.isInitialized = true;
    },
    isItemDeprecated(item) {
      // Check if the item itself is deprecated
      if (item.isDeprecated) {
        return true;
      }

      // Check if parent Application is deprecated (only for grouped dropdowns)
      if (this.grouperDropdown && item.groupDropdownValueId) {
        const parentApplication = this.grouperDropdown.valueList.find(
          value => value.id === item.groupDropdownValueId
        );
        return parentApplication?.isDeprecated || false;
      }

      return false;
    },
    removeItem(item) {
      if (this.multiple && Array.isArray(this.selectedValue)) {
        this.selectedValue = this.selectedValue.filter(id => id !== item.id);
        return;
      }

      if (!this.multiple && this.selectedValue === item.id) {
        this.selectedValue = null;
      }
    },
    resetCreateForm() {
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
    },
    ...mapActions({
      getDropdownList: "admin/dropdown/list",
    }),
  },
  props: {
    attributeName: {
      type: String,
      required: true,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    multiple: {
      default: true,
      type: Boolean,
    },
    value: {},
  },
  watch: {
    attributeName(val) {
      this.initialize();
    },
    selectedValue(val) {
      this.$emit("input", val);
    },
    value(val) {
      this.selectedValue = val;
    },
  },
});
</script>

<style scoped>
.deprecated-label {
  font-size: 0.85em;
  color: grey;
  font-weight: normal;
}
</style>
